/*
 * Transaction Monitoring — MOCK DATA (frontend-only preview).
 *
 * These arrays mirror the shapes the real backend actions will return:
 *   - list-evaluations  -> evaluations[]   (one row per tm_evaluation_log entry)
 *   - get-logs          -> activityLogs[]  (unified across products: peddlr_lite + peddlr)
 *   - list-documents-history -> documentsHistory keyed by cognito_sub
 *   - get-balance       -> merchantBalances keyed by cognito_sub
 *
 * NOTE: all data here is fake and for UI/flow preview only. No backend is wired.
 */

// Action-badge map: 1:1 with the VM `ACTION` enum, humanized labels. `severity` drives badge color.
export const ACTION_BADGES = {
  soft_flag: { label: "Soft Flag", severity: "low" },
  enhanced_review: { label: "Enhanced Review", severity: "medium" },
  flag: { label: "Flag", severity: "medium" },
  hold: { label: "Hold", severity: "high" },
  step_up: { label: "Step Up", severity: "medium" },
  restrict_outflow: { label: "Restrict Outflow", severity: "high" },
  throttle: { label: "Throttle", severity: "medium" },
  block: { label: "Block", severity: "critical" },
  freeze_inbound_cap: { label: "Freeze Inbound Cap", severity: "critical" },
};

// Friendly rule labels (code -> human). Mirrors the VM RULE ids.
export const RULE_LABELS = {
  "TM-VEL-001": "Inbound Rate",
  "TM-VEL-002": "Inbound Daily Cumulative",
  "TM-VEL-003": "Inbound Monthly Ceiling",
  "TM-VEL-004": "Outbound Rate",
  "TM-VEL-005": "Outbound Daily Count",
  "TM-PAT-001": "Structuring",
  "TM-PAT-002": "Rapid Succession",
  "TM-PAT-004": "Dormant Account Spike",
  "TM-DIS-002": "Unregistered/Changed Bank",
  "TM-DIS-003": "Abnormal Payout Frequency",
};

// Tier (data value) -> UI label. corporate shows as "Enterprise".
export const TIER_LABELS = {
  micro: "Micro",
  small: "Small",
  medium: "Medium",
  corporate: "Enterprise",
};

export const FREEZE_REASONS = [
  "Suspicious Transactions",
  "Fraudulent Activity",
  "Hacked Account",
  "Bugs/Issues",
  "Need Additional Requirements",
];

export const UNFREEZE_REASONS = [
  "Cleared by Management",
  "Identified as Bugs/Issue",
  "No Hacking Identified",
  "Cleared from BSP Watchlist",
];

export const ACCOUNT_ACTIONS = [
  { value: "account_enable", text: "Account Enable" },
  { value: "temporary_enable", text: "Temporary Enable" },
  // Account Close is deferred (not offered this iteration).
];

export const ACCOUNT_TYPES = [
  { value: "micro", text: "Micro" },
  { value: "small", text: "Small" },
  { value: "medium", text: "Medium" },
  { value: "corporate", text: "Enterprise" },
];

/*
 * One row per evaluation (tm_evaluation_log). The same merchant (cognito_sub) can appear in
 * multiple rows; wallet_status is a merchant-level property so it is the SAME across a merchant's rows.
 */
export const evaluations = [
  {
    eval_uuid: "ev-0001",
    merchant_wallet_id: 101,
    cognito_sub: "sub-joel",
    product: "peddlr_lite",
    account_number: "794434102",
    account_name: "Joel Cruz",
    business_name: "Joel Sari Sari Store",
    rule_id: "TM-VEL-001",
    action: "soft_flag",
    kyc_tier: "small",
    wallet_status: "enabled",
    created_at: "2026-10-05 09:14:00",
  },
  {
    eval_uuid: "ev-0002",
    merchant_wallet_id: 102,
    cognito_sub: "sub-norman",
    product: "peddlr_lite",
    account_number: "912643678",
    account_name: "Norman Natividad",
    business_name: "MrExtraRice",
    rule_id: null,
    action: null,
    kyc_tier: "small",
    wallet_status: "enabled",
    created_at: "2026-10-05 08:51:00",
  },
  {
    eval_uuid: "ev-0003",
    merchant_wallet_id: 103,
    cognito_sub: "sub-luis",
    product: "peddlr_lite",
    account_number: "823201785",
    account_name: "Luis Manshanas",
    business_name: "Luis Store",
    rule_id: "TM-VEL-002",
    action: "enhanced_review",
    kyc_tier: "medium",
    wallet_status: "freeze",
    created_at: "2026-10-05 08:40:00",
  },
  {
    eval_uuid: "ev-0004",
    merchant_wallet_id: 104,
    cognito_sub: "sub-antonio",
    product: "peddlr_lite",
    account_number: "980835528",
    account_name: "Antonio Gwaba",
    business_name: "Antea Shop",
    rule_id: "TM-PAT-004",
    action: "restrict_outflow",
    kyc_tier: "micro",
    wallet_status: "freeze",
    created_at: "2026-10-05 08:22:00",
  },
  {
    eval_uuid: "ev-0005",
    merchant_wallet_id: 105,
    cognito_sub: "sub-fred",
    product: "peddlr",
    account_number: "558053389",
    account_name: "Fred Bronsumo",
    business_name: "Fred Milk Shop",
    rule_id: "TM-DIS-002",
    action: "hold",
    kyc_tier: "small",
    wallet_status: "freeze",
    created_at: "2026-10-05 07:58:00",
  },
  {
    eval_uuid: "ev-0006",
    merchant_wallet_id: 106,
    cognito_sub: "sub-junnel",
    product: "peddlr",
    account_number: "625438798",
    account_name: "Junnel Tide",
    business_name: "Jun Store",
    rule_id: null,
    action: null,
    kyc_tier: "corporate",
    wallet_status: "enabled",
    created_at: "2026-10-05 07:30:00",
  },
  {
    eval_uuid: "ev-0007",
    merchant_wallet_id: 107,
    cognito_sub: "sub-ariel",
    product: "peddlr_lite",
    account_number: "166684771",
    account_name: "Ariel Lababo",
    business_name: "Ariel Store",
    rule_id: "TM-DIS-003",
    action: "flag",
    kyc_tier: "corporate",
    wallet_status: "enabled",
    created_at: "2026-10-05 07:12:00",
  },
  {
    // Same merchant as ev-0001 (Joel) — a second evaluation. Shares Joel's wallet_status.
    eval_uuid: "ev-0008",
    merchant_wallet_id: 101,
    cognito_sub: "sub-joel",
    product: "peddlr_lite",
    account_number: "794434102",
    account_name: "Joel Cruz",
    business_name: "Joel Sari Sari Store",
    rule_id: "TM-PAT-002",
    action: "block",
    kyc_tier: "small",
    wallet_status: "enabled",
    created_at: "2026-10-04 22:05:00",
  },
  {
    eval_uuid: "ev-0009",
    merchant_wallet_id: 108,
    cognito_sub: "sub-joy",
    product: "peddlr",
    account_number: "233484644",
    account_name: "Joy Plato",
    business_name: "Joy Store",
    rule_id: "TM-VEL-003",
    action: "freeze_inbound_cap",
    kyc_tier: "corporate",
    wallet_status: "enabled",
    created_at: "2026-10-04 20:41:00",
  },
  {
    eval_uuid: "ev-0010",
    merchant_wallet_id: 109,
    cognito_sub: "sub-maria",
    product: "peddlr_lite",
    account_number: "445120983",
    account_name: "Maria Santos",
    business_name: "Maria Mini Mart",
    rule_id: "TM-VEL-004",
    action: "throttle",
    kyc_tier: "medium",
    wallet_status: "enabled",
    created_at: "2026-10-04 19:03:00",
  },
];

/*
 * Unified activity log across BOTH products. One row per freeze/unfreeze event.
 * `modified_by` blank => system (VM auto-freeze); otherwise the acting ERP user.
 */
export const activityLogs = [
  {
    id: 1,
    product: "peddlr_lite",
    account_number: "823201785",
    account_name: "Luis Manshanas",
    business_name: "Luis Store",
    action: "freeze",
    reason: "Cumulative Inflows > ₱500,000.00",
    modified_by: "",
    created_at: "November 22, 2023 at 04:22 PM",
  },
  {
    id: 2,
    product: "peddlr_lite",
    account_number: "980835528",
    account_name: "Antonio Gwaba",
    business_name: "Antea Shop",
    action: "freeze",
    reason: ">6x daily avg volume, Rolling 30 days",
    modified_by: "",
    created_at: "November 22, 2023 at 04:22 PM",
  },
  {
    id: 3,
    product: "peddlr",
    account_number: "558053389",
    account_name: "Fred Bronsumo",
    business_name: "Fred Milk Shop",
    action: "freeze",
    reason: "Bank changed <7 days, ≥₱10,000 Payout",
    modified_by: "",
    created_at: "November 22, 2023 at 04:22 PM",
  },
  {
    id: 4,
    product: "peddlr_lite",
    account_number: "794434102",
    account_name: "Joel Cruz",
    business_name: "Joel Sari Sari Store",
    action: "enabled",
    reason: "Cleared by Management",
    modified_by: "john.doe@email.com",
    created_at: "November 22, 2023 at 04:22 PM",
  },
  {
    id: 5,
    product: "peddlr_lite",
    account_number: "794434102",
    account_name: "Joel Cruz",
    business_name: "Joel Sari Sari Store",
    action: "freeze",
    reason: "Suspicious Transactions",
    modified_by: "john.doe@email.com",
    created_at: "November 22, 2023 at 03:22 PM",
  },
  {
    id: 6,
    product: "peddlr",
    account_number: "233484644",
    account_name: "Joy Plato",
    business_name: "Joy Store",
    action: "freeze",
    reason: "Fraudulent Activity",
    modified_by: "jane.cruz@peddlr.ph",
    created_at: "November 21, 2023 at 05:10 PM",
  },
];

// Documents history per merchant (keyed by cognito_sub). Mirrors list-documents-history.
export const documentsHistory = {
  "sub-joel": [
    {
      created_at: "Sep 18, 2026, 10:32 AM",
      modified_by: "user_support@email.com",
      reason: "No Hacking Identified",
      account_action: "Account Enable",
      status: "enabled",
      account_type: "micro",
      remarks: "--",
      reason_for_freeze: null,
      documents: [
        { file_name: "Government_ID_Verification.png", size_label: "2.4 MB" },
        { file_name: "Notarized_Homonym_Affidavit.pdf", size_label: "5.4 MB" },
      ],
    },
    {
      created_at: "Sep 16, 2026, 3:00 PM",
      modified_by: "user_support@email.com",
      reason: "Hacked Account",
      account_action: null,
      status: "freeze",
      account_type: null,
      remarks: "--",
      reason_for_freeze: "--",
      documents: [],
    },
  ],
};

// Mock remaining balance per merchant (keyed by cognito_sub). Mirrors get-balance.
export const merchantBalances = {
  "sub-joel": 5250.0,
  "sub-luis": 18240.5,
  "sub-antonio": 920.75,
  "sub-fred": 44110.0,
  "sub-joy": 150300.0,
};

// Simulate network latency for a realistic feel.
export const mockDelay = (ms = 400) => new Promise((r) => setTimeout(r, ms));
